import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Router } from '@angular/router';

import { CurationService } from '../../../admin/curation/curation.service';
import { CurationStats, CurationStatsFilters } from '../../../admin/curation/interfaces/curation.interface';
import { SmartReviewPermissionsService } from '../../../core/services/smart-review-permissions.service';
import { normalizeSeverity } from '../../../review/utils/severity.util';
import { formatCount, formatSharePercent } from '../../utils/review-stats-format.util';

/** Uma barra de falso positivo por severidade. */
export interface CalibrationSeverityBar {
  label: string;
  modifier: 'high' | 'medium' | 'low';
  width: number;
  value: string;
  votes: string;
}

/** Abaixo disso a taxa de falso positivo representa poucos achados, e o card avisa. */
const LOW_COVERAGE = 0.2;

/**
 * Pontos percentuais de falso positivo que a ALTA precisa ficar abaixo da MÉDIA para o card
 * considerar que a severidade ordena. Abaixo disso a diferença é ruído de amostra.
 */
const SEVERITY_ORDERING_MARGIN = 10;

/**
 * Calibragem na visão de serviço: cobertura de feedback do recorte e falso positivo por
 * severidade. A taxa geral de falso positivo não se repete aqui — é o indicador do topo,
 * que lê estes mesmos votos pelo `loaded`. O card fica com o que qualifica aquele número:
 * quantos achados receberam voto e se a severidade está ordenando o que corrigir.
 *
 * Para um revisor por IA é o número que decide se a ferramenta é útil ou ruído, e sem ele
 * a proporção de severidades não diz se é real ou calibragem ruim. Não recalcula nada: lê
 * `GET /admin/curation/stats`, que já entrega votos, cobertura e votos por severidade, e
 * leva ao Painel de Curadoria — é resumo com link, não uma segunda fonte.
 */
@Component({
  selector: 'app-review-stats-calibration',
  templateUrl: './review-stats-calibration.component.html',
  styleUrls: ['./review-stats-calibration.component.css']
})
export class ReviewStatsCalibrationComponent implements OnChanges {
  @Input() dateFrom?: string;
  @Input() dateTo?: string;
  /** Preset do período do dashboard, para o painel abrir no mesmo período. */
  @Input() periodPreset?: string;
  /**
   * Os mesmos recortes do dashboard. Sem eles a calibragem mostrava o time inteiro com a
   * área escolhida no filtro — "604 de 3.846" lido como se fosse da área (decisão 0033).
   */
  @Input() sourceProviders: string[] = [];
  @Input() areas: string[] = [];
  /** Usuário do dashboard = quem pediu o review. */
  @Input() users: string[] = [];
  @Input() resourceTypes: string[] = [];
  /** Repassa os votos lidos, para o indicador de falso positivo do topo não ler de novo. */
  @Output() loaded = new EventEmitter<CurationStats | null>();

  stats: CurationStats | null = null;
  loading = false;
  loadError = false;

  readonly help =
    'Votos sobre os achados dos reviews que entram nos filtros do dashboard: período, ' +
    'origem, área, usuário (quem pediu o review) e tipo.\n\n' +
    '• Cobertura: achados gerados que receberam ao menos um voto. Cobertura baixa torna ' +
    'o falso positivo pouco representativo.\n' +
    '• Falso positivo: votos "não procede" sobre o total de votos em achados.\n' +
    '• Por severidade: a mesma taxa entre os achados de cada severidade — se ALTA erra ' +
    'tanto quanto MÉDIA, a severidade não está ordenando o que corrigir primeiro. A ' +
    'comparação é com MÉDIA porque BAIXA deixou de ser ação obrigatória e tende a sumir. ' +
    'O aviso é indicativo (diferença menor que 10 pontos); a medição que decide é a da ' +
    'skill de ajuste de prompt, com teste estatístico.';

  readonly lowCoverageHelp =
    'Menos de 20% dos achados receberam voto: o falso positivo representa poucos achados ' +
    'e pode mudar bastante com mais votos.';

  private requestSequence = 0;

  constructor(
    private curationService: CurationService, private router: Router,
    private permissions: SmartReviewPermissionsService
  ) {}

  /**
   * O card aparece pra quem tem visão de serviço (ADMIN ou AUDIT — ver
   * ReviewDashboardComponent.resolveAudience), mas o Painel de Curadoria em si é só
   * ADMIN (SmartReviewAdminGuard). Sem esta checagem, quem só tem AUDIT via um botão
   * que leva a um 403.
   */
  get canOpenCuration(): boolean {
    return this.permissions.hasAdmin();
  }

  ngOnChanges(): void {
    this.loading = true;
    this.loadError = false;
    const request = ++this.requestSequence;
    const filters: CurationStatsFilters = {};
    if (this.dateFrom) { filters.dateFrom = this.dateFrom; }
    if (this.dateTo) { filters.dateTo = this.dateTo; }
    if (this.sourceProviders?.length) { filters.sourceProvider = this.sourceProviders; }
    if (this.areas?.length) { filters.area = this.areas; }
    if (this.users?.length) { filters.requestedBy = this.users; }
    if (this.resourceTypes?.length) { filters.resourceType = this.resourceTypes; }
    this.curationService.getStats(filters).subscribe({
      next: stats => {
        if (request !== this.requestSequence) { return; }
        this.stats = stats;
        this.loading = false;
        this.loaded.emit(stats);
      },
      error: () => {
        if (request !== this.requestSequence) { return; }
        this.stats = null;
        this.loading = false;
        this.loadError = true;
        this.loaded.emit(null);
      }
    });
  }

  get hasVotes(): boolean {
    return (this.stats?.votes?.total ?? 0) > 0;
  }

  /** Fração dos achados com voto, ou `null` sem achados no período. */
  get coverage(): number | null {
    const findings = this.stats?.coverage?.findings;
    return findings?.total ? findings.withFeedback / findings.total : null;
  }

  get coverageLabel(): string {
    const findings = this.stats?.coverage?.findings;
    return findings?.total ? formatSharePercent(findings.withFeedback, findings.total) : '—';
  }

  get coverageDetail(): string {
    const findings = this.stats?.coverage?.findings;
    if (!findings?.total) { return ''; }
    return `${formatCount(findings.withFeedback)} de ${formatCount(findings.total)} achados receberam voto`;
  }

  get coverageWidth(): number {
    return Math.round((this.coverage ?? 0) * 100);
  }

  get lowCoverage(): boolean {
    const coverage = this.coverage;
    return coverage != null && coverage < LOW_COVERAGE;
  }

  /** FP por severidade, na ordem ALTA → BAIXA; severidade sem voto não aparece. */
  get bySeverity(): CalibrationSeverityBar[] {
    const order: Record<string, { rank: number; label: string; modifier: CalibrationSeverityBar['modifier'] }> = {
      ALTA: { rank: 0, label: 'Alta', modifier: 'high' },
      MEDIA: { rank: 1, label: 'Média', modifier: 'medium' },
      BAIXA: { rank: 2, label: 'Baixa', modifier: 'low' }
    };
    return (this.stats?.votesBySeverity ?? [])
      .map(bucket => ({ bucket, meta: order[normalizeSeverity(bucket.key)] }))
      .filter(({ bucket, meta }) => !!meta && bucket.total > 0)
      .sort((a, b) => a.meta.rank - b.meta.rank)
      .map(({ bucket, meta }) => ({
        label: meta.label,
        modifier: meta.modifier,
        width: Math.round((bucket.falsePositive / bucket.total) * 100),
        value: formatSharePercent(bucket.falsePositive, bucket.total),
        votes: `${formatCount(bucket.falsePositive)} de ${formatCount(bucket.total)} votos`
      }));
  }

  /**
   * ALTA sem errar claramente menos que MÉDIA: a severidade não separa o que corrigir
   * primeiro. É a leitura que as barras pedem, dita em palavras.
   *
   * Compara com MÉDIA, e não com BAIXA: desde que BAIXA deixou de ser ação obrigatória ela
   * tende a sumir, e o alerta ficaria mudo justamente quando ALTA e MÉDIA erram igual. A
   * margem existe porque "ALTA 54%, MÉDIA 55%" é empate, não ordem.
   */
  get severityNotOrdering(): boolean {
    const bars = this.bySeverity;
    const high = bars.find(b => b.modifier === 'high');
    const medium = bars.find(b => b.modifier === 'medium');
    return !!high && !!medium && high.width > medium.width - SEVERITY_ORDERING_MARGIN;
  }

  /** `bySeverity` monta objetos novos a cada ciclo: sem isto o ngFor recriaria as barras. */
  trackByLabel(_index: number, bar: CalibrationSeverityBar): string {
    return bar.label;
  }

  /** Abre o Painel de Curadoria no mesmo recorte: as duas telas respondem pelos mesmos reviews. */
  openCuration(): void {
    const queryParams: Record<string, string | string[]> = {};
    if (this.periodPreset) { queryParams['period'] = this.periodPreset; }
    if (this.dateFrom) { queryParams['dateFrom'] = this.dateFrom; }
    if (this.dateTo) { queryParams['dateTo'] = this.dateTo; }
    if (this.sourceProviders?.length) { queryParams['sourceProvider'] = this.sourceProviders; }
    if (this.areas?.length) { queryParams['area'] = this.areas; }
    if (this.users?.length) { queryParams['requestedBy'] = this.users; }
    if (this.resourceTypes?.length) { queryParams['resourceType'] = this.resourceTypes; }
    this.router.navigate(['/menu/smart-review/admin/curation/stats'], { queryParams });
  }
}
